import { useMemo } from "react"
import cn from "classnames"

import { CURRENCY } from "utils/constants"
import { DiscountProps, PriceProps, UniquePrices } from "components/PriceTemplate/interfaces"
import { getProperServicePrice } from "components/PriceTemplate/helpers/getProperServicePrice"
import style from "components/PriceTemplate/style.module.scss"
import { useClientContext } from "modules/ClientAuthorization/common/ClientContext"
import { useDiscountViewData } from "modules/discount"
import { Psychologist, OfferedService } from "utils/interfaces"
import { formatPrice } from "utils/utils"
import { uniq } from "utils/infrastructure/uniq"
import { getPublicServices } from "utils/helpers/psychologistUtil"

function getPrices(services: OfferedService[]) {
    return getPublicServices(services).map(({ price }: OfferedService) => price)
}

function useGetUniquePrices(services?: OfferedService[], psy?: Psychologist): UniquePrices {
    return useMemo(() => {
        if (services?.length === 0 || psy?.offered_services === undefined) {
            return { uniquePrices: [] }
        }

        const prices = services ? getPrices(services) : getPrices(psy.offered_services)
        const uniquePrices = uniq(prices)
        return { uniquePrices }
    }, [services, psy])
}

function renderFromText(
    singleServicePrice: number | undefined,
    uniquePrices: number[],
    capitalLetter: boolean
): JSX.Element | null {
    const shouldShowFrom =
        !singleServicePrice && singleServicePrice !== 0 && uniquePrices.length > 1
    if (shouldShowFrom) {
        return <>{capitalLetter ? "От" : "от"}&nbsp;</>
    }
    return null
}

export function StrikeOutPrice() {
    return (
        <div className={style.strikeOut}>
            <div className={style.strikeOutWhite} />
            <div className={style.strikeOutRed} />
            <div className={style.strikeOutWhite} />
        </div>
    )
}

export function Price({
    className,
    psy,
    singleServicePrice,
    services,
    currency = CURRENCY.RUB,
    capitalLetter = true,
    deposit,
    singleDeposit,
    depositWithDiscount,
    ...props
}: PriceProps) {
    const [{ isVerified }] = useClientContext()
    const servicePrice = getProperServicePrice(psy.offered_services, services, singleServicePrice)
    const {
        hasDiscount,
        price: [oldPrice, newPrice],
    } = useDiscountViewData(psy, servicePrice)

    const { uniquePrices } = useGetUniquePrices(services, psy)

    const getPrice = () => {
        if (isVerified) {
            if (deposit && servicePrice) {
                const discount = (servicePrice * deposit.percentage) / 100
                const adjustedPrice = depositWithDiscount ? servicePrice - discount : servicePrice
                return adjustedPrice * (singleDeposit ? 1 : deposit.session_amount)
            }
            return servicePrice
        }

        return hasDiscount ? newPrice : oldPrice
    }

    const price = getPrice()

    if (price !== undefined) {
        return (
            <span className={cn(style.container, className)} data-testid="price" {...props}>
                {renderFromText(singleServicePrice, uniquePrices, capitalLetter)}
                <span className="is-nowrap">{formatPrice(price, currency)}</span>
            </span>
        )
    }

    return null
}

export function Discount({
    className,
    psy,
    showDiscountInBooking = false,
    singleServicePrice,
    services,
    currency = CURRENCY.RUB,
    capitalLetter = true,
    deposit,
    showStrikeOut = true,
}: DiscountProps) {
    const servicePrice = getProperServicePrice(psy.offered_services, services, singleServicePrice)
    const [{ isVerified }] = useClientContext()

    const {
        hasDiscount,
        price: [oldPrice],
    } = useDiscountViewData(psy, servicePrice)

    const { uniquePrices } = useGetUniquePrices(services, psy)

    const showDiscountedPrice = (hasDiscount && !isVerified) || showDiscountInBooking

    if (deposit && oldPrice) {
        return (
            <span className={cn(style.container, className)} data-testid="discount">
                {formatPrice(oldPrice * deposit.session_amount, currency)}
                {showStrikeOut && <StrikeOutPrice />}
            </span>
        )
    }

    if (showDiscountedPrice && oldPrice) {
        return (
            <span className={cn(style.container, className)} data-testid="discount">
                {renderFromText(singleServicePrice, uniquePrices, capitalLetter)}
                {formatPrice(oldPrice, currency)}
                {showStrikeOut && <StrikeOutPrice />}
            </span>
        )
    }

    return null
}
